import { TransitionEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLead from 'src/hooks/useLead';
import { ILead } from 'src/interfaces/lead';
import { CHAT_SCOPE } from 'src/contexts/assistant/constants';
import {
    ASSISTANT_SECTION,
    GREETING_KEYS,
    SHELL_HEIGHT_PROPERTY,
    TAssistantSection,
} from 'src/modules/assistant/constants/widget';
import { formatAssistantDates } from 'src/modules/assistant/helpers/formatAssistantDates';
import { TChatScope } from 'src/contexts/assistant/types';
import { useAssistantContext } from './useAssistantContext';
import { useAutoScroll } from './useAutoScroll';
import { useAvailableActions } from './useAvailableActions';
import { useEscapeKey } from './useEscapeKey';
import { useGreetingAnimation } from './useGreetingAnimation';
import { useMessageActions } from './useMessageActions';
import { useResponseReady } from './useResponseReady';
import { useResponseSound } from './useResponseSound';

/**
 * State and handlers of the assistant widget, so the component only composes the view.
 */
export const useAssistantWidget = () => {
    const { t, i18n } = useTranslation();
    const { lead } = useLead() as { lead: ILead | null };
    const assistant = useAssistantContext();
    const { context, selectedLead, messages, busy, loading, error } = assistant;
    const [open, setOpen] = useState(false);
    const [section, setSection] = useState<TAssistantSection>(
        ASSISTANT_SECTION.CHAT
    );
    const [composerKey, setComposerKey] = useState(0);
    const [listening, setListening] = useState(false);
    const launcherRef = useRef<HTMLButtonElement>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const inChat = section === ASSISTANT_SECTION.CHAT;
    const requestDisabled = busy || loading;

    const sound = useResponseSound();
    const responseReady = useResponseReady({
        busy,
        error,
        open,
        onResponse: sound.play,
    });
    const greeting = useGreetingAnimation({
        enabled: !open,
        messages: GREETING_KEYS.map((key) => t(key)),
        language: i18n.language,
    });
    const {
        loadedLead,
        leadRequiresAgent,
        channel,
        channelConversation,
        getAvailableActions,
    } = useAvailableActions({ lead, selectedLead, scope: context.scope });
    const scrollTranscript = useAutoScroll(
        transcriptRef,
        open && !loading && inChat,
        [context.scope, messages]
    );
    useEscapeKey(open, () => {
        setOpen(false);
        launcherRef.current?.focus();
    });

    const resetComposer = () => setComposerKey((key) => key + 1);
    const submitMessage = (text: string) => {
        if (!text.trim()) return;
        sound.prepare();
        assistant.sendMessage(text);
    };
    const requestNextAction = () => {
        if (leadRequiresAgent) return;
        sound.prepare();
        assistant.determineNextAction();
    };
    const messageActions = useMessageActions({
        channel: channel?.channel || null,
        channelConversation,
        loadedLead,
        selectedLead,
        scope: context.scope,
        leadId: context.leadId,
        requestDisabled,
        getAvailableActions,
        rateActionSuggestion: assistant.rateActionSuggestion,
        onRecalculate: requestNextAction,
        onContinue: () => {
            resetComposer();
            submitMessage(t('Assistant.ContinueFollowUpPrompt'));
        },
    });

    return {
        assistant,
        open,
        section,
        inChat,
        composerKey,
        listening,
        launcherRef,
        transcriptRef,
        requestDisabled,
        responseReady,
        greeting,
        leadRequiresAgent,
        messageActions,
        showNextActionSuggestion:
            inChat &&
            assistant.canUseAssistant &&
            Boolean(selectedLead) &&
            context.scope === CHAT_SCOPE.LEAD &&
            !leadRequiresAgent,
        formatDateText: (value?: string) =>
            formatAssistantDates(value, i18n.language),
        openWidget: () => setOpen(true),
        closeWidget: () => setOpen(false),
        setListening,
        submitMessage,
        requestNextAction,
        selectChat: (scope: TChatScope) => {
            resetComposer();
            setSection(ASSISTANT_SECTION.CHAT);
            assistant.setChatScope(scope);
        },
        selectWorkingLeads: () => {
            resetComposer();
            setSection(ASSISTANT_SECTION.WORKING_LEADS);
            assistant.loadWorkingLeads({ reset: true });
        },
        loadMoreWorkingLeads: () => assistant.loadWorkingLeads(),
        handleShellTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => {
            if (
                open &&
                event.currentTarget === event.target &&
                event.propertyName === SHELL_HEIGHT_PROPERTY
            )
                scrollTranscript();
        },
    };
};
